import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CreditCard, FileDown, Loader2, RotateCw } from 'lucide-react';
import { getPublicInvoice, publicInvoicePdfUrl } from '@/api/invoices';
import { cents, dayLabel } from '@/components/invoices/invoiceFormat';
import useGoogleFonts from '@/components/settings/look/useGoogleFonts';

const TONE = { paid: 'bg-[#E8F0EA] text-[#4A7C59]', overdue: 'bg-[#F4DEDE] text-[#B45353]', void: 'bg-[#F4DEDE] text-[#B45353]' };

function Shell({ children }) {
  return <div className="min-h-screen bg-[#EEF0F3] px-4 py-8 sm:py-14" data-testid="invoice-public-page"><div className="mx-auto max-w-2xl">{children}</div></div>;
}

function Dead({ retry }) {
  return (
    <div className="rounded-md border border-[#D5D8DD] bg-white p-8 text-center" data-testid="invoice-public-dead">
      <h1 className="font-exo text-xl font-bold text-[#0D1B2A]">This invoice link isn't available</h1>
      <p className="mt-2 text-sm text-[#5C6370]">It may have expired or been replaced. Please contact the company that sent it for a new link.</p>
      {retry && <button type="button" onClick={retry} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#4575A8]" data-testid="invoice-public-retry"><RotateCw className="h-4 w-4" />Try again</button>}
    </div>
  );
}

const hf = (v) => ({ fontFamily: v.company.fonts?.heading });
const block = (v, key) => (v.look?.blocks || []).find((b) => b.key === key);

// Online payments QuickBooks has, not yet confirmed by the company: the client sees them as being processed.
function PendingNote({ v }) {
  if (!v.pending_payments?.length) return null;
  return (
    <div className="mt-4 rounded-sm border border-[#4A7C59]/30 bg-[#E8F0EA]/60 px-3 py-2 text-xs text-[#3A5E45]" data-testid="invoice-public-pending">
      {v.pending_payments.map((p, i) => <p key={i}>{dayLabel(p.paid_on)} · {cents(p.amount_cents)} paid online · being processed</p>)}
      <p className="mt-0.5 text-[11px] text-[#5C6370]">Thank you. The balance updates once {v.company.name} confirms it.</p>
    </div>
  );
}

function Header({ v }) {
  const c = v.company;
  const badge = v.look?.status_badge !== false && v.status_label;
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 border-b-[3px] pb-5" style={{ borderColor: c.primary }}>
      <div className="min-w-0">
        {c.logo && <img src={c.logo} alt="" className="mb-2 max-h-14 max-w-[220px] object-contain" data-testid="invoice-public-logo" />}
        <p className="font-exo text-lg font-bold text-[#0D1B2A]" style={hf(v)} data-testid="invoice-public-company">{c.name}</p>
        <p className="text-xs text-[#5C6370]">{[c.address, c.phone, c.email, c.website].filter(Boolean).join(' · ')}</p>
      </div>
      <div className="text-right">
        <p className="font-exo text-2xl font-bold tracking-[0.06em]" style={{ color: c.primary, ...hf(v) }}>INVOICE</p>
        <p className="text-sm font-semibold tabular-nums text-[#0D1B2A]" data-testid="invoice-public-label">{v.label}</p>
        {badge && <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.08em] ${TONE[v.status] || 'bg-[#E6E8EC] text-[#111417]'}`} data-testid="invoice-public-status">{v.status_label}</span>}
      </div>
    </div>
  );
}

function Meta({ v }) {
  const items = [['Bill to', v.bill_to.name || '—', [v.bill_to.address, v.bill_to.email].filter(Boolean).join('\n')],
    ['Project', v.project, v.project_address], ['Invoice date', v.issued_on && dayLabel(v.issued_on)], ['Terms', v.terms],
    ['Due', v.due_on && dayLabel(v.due_on)], ['PO #', v.po_number], ['Job #', v.job_number]].filter(([, val]) => val);
  return (
    <div className="grid grid-cols-2 gap-4 py-5 sm:grid-cols-4 [&_p]:whitespace-pre-line" data-testid="invoice-public-meta">
      {items.map(([k, val, sub]) => (
        <div key={k}><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A8F99]">{k}</p>
          <p className="text-sm font-semibold text-[#0D1B2A]">{val}</p>{sub && <p className="text-xs text-[#5C6370]">{sub}</p>}</div>
      ))}
    </div>
  );
}

// The invoice's text blocks as the company set them (how to pay, notes, thank-you).
function Blocks({ v }) {
  const remit = block(v, 'remit');
  const notes = block(v, 'notes');
  const thanks = block(v, 'thanks');
  return (
    <>
      {remit && <div className="mt-5 whitespace-pre-line border-l-[3px] bg-[#F4F4F1] px-4 py-3 text-xs text-[#111417]" style={{ borderColor: v.company.accent }} data-testid="invoice-public-remit"><p className="mb-1 text-sm font-bold" style={hf(v)}>{remit.title || 'How to pay'}</p>{remit.text}</div>}
      {notes && <div className="mt-4 whitespace-pre-line text-xs text-[#111417]" data-testid="invoice-public-notes"><p className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: v.company.accent }}>{notes.title || 'Notes'}</p>{notes.text}</div>}
      {thanks && <p className="mt-6 text-center text-base italic" style={{ color: v.company.primary, ...hf(v) }} data-testid="invoice-public-thanks">{thanks.text}</p>}
    </>
  );
}

function Body({ v, token }) {
  useGoogleFonts(v.company.fonts?.google);
  return (
    <div className="rounded-md border border-[#D5D8DD] bg-white p-6 shadow-sm sm:p-8" style={{ fontFamily: v.company.fonts?.body }} data-testid="invoice-public-card">
      <Header v={v} />
      <Meta v={v} />
      <div className="divide-y divide-[#E6E8EC] border-y border-[#D5D8DD]" data-testid="invoice-public-lines">
        {v.lines.map((l, i) => (
          <div key={i} className="flex items-baseline justify-between gap-4 py-3 text-sm">
            <span><span className="text-[#0D1B2A]">{l.description}</span>{l.detail && <span className="block text-xs text-[#5C6370]">{l.detail}</span>}</span>
            <span className="tabular-nums">{cents(l.amount_cents)}</span>
          </div>
        ))}
      </div>
      <div className="ml-auto mt-4 w-full max-w-xs space-y-1 text-sm">
        <div className="flex justify-between"><span>Total</span><span className="tabular-nums" data-testid="invoice-public-total">{cents(v.total_cents)}</span></div>
        {v.paid_cents > 0 && <div className="flex justify-between text-[#4A7C59]"><span>Paid</span><span className="tabular-nums">−{cents(v.paid_cents)}</span></div>}
        <div className="flex justify-between border-t-2 pt-2 text-lg font-bold" style={{ borderColor: v.company.primary }}><span>Balance due</span><span className="tabular-nums" data-testid="invoice-public-balance">{cents(v.balance_cents)}</span></div>
      </div>
      {v.payments.length > 0 && <div className="mt-4 space-y-0.5 text-xs text-[#5C6370]" data-testid="invoice-public-payments">{v.payments.map((p, i) => <p key={i}>{dayLabel(p.paid_on)} · payment received · {cents(p.amount_cents)}</p>)}</div>}
      <PendingNote v={v} />
      <Blocks v={v} />
      {v.company.footer && <p className="mt-6 whitespace-pre-line border-t border-[#E6E8EC] pt-3 text-xs text-[#5C6370]">{v.company.footer}</p>}
      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <a href={publicInvoicePdfUrl(token)} className={`inline-flex h-10 items-center gap-2 rounded-sm px-4 text-sm font-semibold transition-opacity hover:opacity-90 ${v.pay_url ? 'border bg-white' : 'text-white'}`}
          style={v.pay_url ? { borderColor: v.company.primary, color: v.company.primary } : { background: v.company.primary }} data-testid="invoice-public-pdf">
          <FileDown className="h-4 w-4" />Download PDF</a>
        {v.pay_url && (
          <a href={v.pay_url} target="_blank" rel="noopener noreferrer" className="inline-flex h-10 items-center gap-2 rounded-sm px-5 text-sm font-semibold text-white transition-opacity hover:opacity-90" style={{ background: v.company.primary }} data-testid="invoice-public-pay-online">
            <CreditCard className="h-4 w-4" />Pay online · {cents(v.balance_cents)}</a>
        )}
      </div>
      {v.pay_url && <p className="mt-2 text-right text-[11px] text-[#8A8F99]" data-testid="invoice-public-pay-note">Card or bank transfer, on a secure QuickBooks page.</p>}
    </div>
  );
}

// A client's private invoice link (Loop 15): the invoice as sent, plus its PDF. No login; the token is the key.
export default function InvoicePublicPage() {
  const { token } = useParams();
  const [state, setState] = useState({ phase: 'loading' });
  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try { setState({ phase: 'ok', v: await getPublicInvoice(token) }); } catch (e) {
      setState({ phase: 'dead', retry: ![404, 410].includes(e?.response?.status) });
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (state.v) document.title = `${state.v.label} · ${state.v.company.name}`; }, [state.v]);
  return (
    <Shell>
      {state.phase === 'loading' && <div className="flex items-center justify-center gap-2 py-20 text-sm text-[#5C6370]" data-testid="invoice-public-loading"><Loader2 className="h-4 w-4 animate-spin" />Loading the invoice…</div>}
      {state.phase === 'dead' && <Dead retry={state.retry ? load : null} />}
      {state.phase === 'ok' && <Body v={state.v} token={token} />}
    </Shell>
  );
}
